import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Group, SettingsResponse, SignedUrlResponse } from '@cloudsign/shared';
import { z } from 'zod';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useToastOnSuccess } from '../../components/Toast';

export const GroupList = z.array(Group);

/** Time zone names for the picker. Falls back to a short list where the browser cannot enumerate them. */
export function timeZoneNames(current?: string): string[] {
  let names: string[] = [];
  try {
    names = (Intl as unknown as { supportedValuesOf(key: string): string[] }).supportedValuesOf('timeZone');
  } catch {
    names = [];
  }
  if (names.length === 0) names = ['Europe/London', 'UTC'];
  if (!names.includes('UTC')) names = [...names, 'UTC'];
  if (current && !names.includes(current)) names = [current, ...names];
  return names;
}

/** One module's settings for a site or group, with a save that sends only the part you give it. */
export function useScopedSettings(base: string, moduleKey: string) {
  const qc = useQueryClient();
  const key = ['settings', base, moduleKey];
  const query = useQuery({ queryKey: key, queryFn: () => api.get(`${base}/${moduleKey}`, { schema: SettingsResponse }) });
  const save = useMutation({
    mutationFn: (patch: Record<string, unknown>) => api.patch(`${base}/${moduleKey}`, { body: patch, schema: SettingsResponse }),
    onSuccess: (r) => {
      qc.setQueryData(key, r);
    },
  });
  useToastOnSuccess(save.isSuccess, t('common.saved'));
  return { query, save, values: query.data?.values };
}

/** Short-lived link to a stored image, or null. */
export function useFileUrl(fileId: string | null | undefined): string | null {
  const q = useQuery({
    queryKey: ['files', 'url', fileId],
    queryFn: () => api.get(`/files/${fileId}/url`, { schema: SignedUrlResponse }),
    enabled: Boolean(fileId),
    staleTime: 30_000,
  });
  return fileId ? (q.data?.url ?? null) : null;
}

/** An object URL for a chosen file that is released when it changes. */
export function useObjectUrl(blob: Blob | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) {
      setUrl(null);
      return;
    }
    const u = URL.createObjectURL(blob);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [blob]);
  return url;
}

/** Reads `values[section]` as an object. */
export function section<T extends object>(values: Record<string, unknown> | undefined, name: string): T {
  const v = values?.[name];
  return (v && typeof v === 'object' ? v : {}) as T;
}
